import {
  AUTO_FILL_MAX_CHILD_PROGRAMS_PER_ROOT,
  AUTO_FILL_PROGRAM_ENTRY_AMOUNT,
} from "../../config/autoFillProgramConfig";

/** Parent AFP must be at least L2 when CO Points are withdrawn to spawn the next serial. */
export const AUTO_FILL_MIN_PROGRAM_LEVEL_FOR_WITHDRAWAL_SPAWN = 2;

/** First AFP (no parent) — only this ID spawns a new serial on withdrawal. */
export const GENESIS_AUTO_FILL_PROGRAM_ID = "AFP-000001";

export function isRootAutoFillProgram(program) {
  return program != null && !program.parentProgramId;
}

/**
 * `users.autoFillPoints` is one bucket per login (parent + withdrawal-spawn children).
 * Only genesis AFP-000001 should mirror that bucket in program wallet UI / validation.
 */
export function usesSharedMemberPointsWalletForProgram(program) {
  if (!isRootAutoFillProgram(program)) return false;
  const seq = Number(program.enrollmentSeq);
  return seq === 1;
}

/** Genesis root or genesis withdrawal-spawn child — L1→L2 uses Admin CO like parent AFP-000001. */
export function qualifiesForGenesisStyleL1ToL2Activation(program) {
  if (!program) return false;
  const seq =
    program.enrollmentSeq ??
    (program.programId?.match(/AFP-(\d+)/)?.[1]
      ? Number(program.programId.match(/AFP-(\d+)/)[1])
      : null);
  if (Number(seq) === 1 && isRootAutoFillProgram(program)) return true;
  return (
    program.spawnedFromWithdrawal === true &&
    program.parentProgramId === GENESIS_AUTO_FILL_PROGRAM_ID
  );
}

export function withdrawalQualifiesForProgramSpawn(withdrawal, program) {
  const level = Number(
    withdrawal?.coPointsLevel ??
      withdrawal?.programLevelAtRequest ??
      program?.currentLevel ??
      1
  );
  return level >= AUTO_FILL_MIN_PROGRAM_LEVEL_FOR_WITHDRAWAL_SPAWN;
}

export function countWithdrawalSpawnedChildren(programs, rootProgramId) {
  if (!rootProgramId) return 0;
  return (programs || []).filter(
    (p) => p.parentProgramId === rootProgramId && p.spawnedFromWithdrawal === true
  ).length;
}

export function canSpawnProgramOnWithdrawal(
  program,
  allUserPrograms,
  withdrawal = null
) {
  if (!isRootAutoFillProgram(program)) {
    return { allowed: false, reason: "not_root_program" };
  }
  if (withdrawal && !withdrawalQualifiesForProgramSpawn(withdrawal, program)) {
    return {
      allowed: false,
      reason: "spawn_requires_program_level_2_plus",
      minLevel: AUTO_FILL_MIN_PROGRAM_LEVEL_FOR_WITHDRAWAL_SPAWN,
    };
  }
  const count = countWithdrawalSpawnedChildren(
    allUserPrograms,
    program.programId
  );
  if (count >= AUTO_FILL_MAX_CHILD_PROGRAMS_PER_ROOT) {
    return {
      allowed: false,
      reason: "max_spawn_reached",
      currentCount: count,
      max: AUTO_FILL_MAX_CHILD_PROGRAMS_PER_ROOT,
    };
  }
  return {
    allowed: true,
    currentCount: count,
    max: AUTO_FILL_MAX_CHILD_PROGRAMS_PER_ROOT,
    entryAmount: AUTO_FILL_PROGRAM_ENTRY_AMOUNT,
  };
}

/**
 * L2+ tranche withdrawal that spawns a child AFP: ₹150 funds the new program entry;
 * only the remainder is paid to Wallet & Points (commission balance).
 */
export function computeWithdrawalWalletPayoutAmount(
  withdrawal,
  program,
  allUserPrograms
) {
  const amount = Number(withdrawal?.amount) || 0;
  if (!program || !isRootAutoFillProgram(program) || amount <= 0) {
    return {
      walletPayout: amount,
      spawnEntryAmount: 0,
      willSpawn: false,
    };
  }
  const spawnCheck = canSpawnProgramOnWithdrawal(
    program,
    allUserPrograms,
    withdrawal
  );
  if (!spawnCheck.allowed) {
    return {
      walletPayout: amount,
      spawnEntryAmount: 0,
      willSpawn: false,
      spawnReason: spawnCheck.reason,
    };
  }
  const entry = AUTO_FILL_PROGRAM_ENTRY_AMOUNT;
  const walletPayout = Math.max(0, amount - entry);
  return {
    walletPayout,
    spawnEntryAmount: Math.min(entry, amount),
    willSpawn: true,
  };
}

export function programIdSortKey(program) {
  const m = String(program?.programId || "").match(/AFP-(\d+)/i);
  if (m) return Number(m[1]);
  const seq = Number(program?.enrollmentSeq);
  if (Number.isFinite(seq) && seq > 0) return seq;
  return Number.MAX_SAFE_INTEGER;
}

/** Admin / member program pickers: strict AFP-00000N numeric order (spawn + roster together). */
export function sortProgramsForDisplay(programs) {
  return [...(programs || [])].sort(
    (a, b) => programIdSortKey(a) - programIdSortKey(b)
  );
}
